import { useState, type FormEvent } from "react";
import { FileUp, Plus, Repeat2 } from "lucide-react";
import { actions, listContracts, listInventory, listLocations } from "@/lib/api";
import { ImportWizard } from "@/components/import-wizard";
import type { Contract, InventoryItem } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useData, matches, reportActionError, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";

export function Inventory({ organisation, refresh, query, can, changed }: ViewProps) {
  const [
    { data: rows, error, loading },
    { data: locations },
    { data: contracts },
  ] = [
    useData(() => listInventory(organisation.id), [organisation.id, refresh]),
    useData(() => listLocations(organisation.id), [organisation.id, refresh]),
    useData(() => listContracts(organisation.id), [organisation.id, refresh]),
  ];
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !rows) return <ErrorState message={error} />;
  const filtered = rows.filter((row) =>
    matches(query, row.name, row.sku, row.location_name, row.failure_reason),
  );

  async function runCycle() {
    setBusy("cycle");
    try {
      const result = await actions.runReplenishmentCycle(organisation.id);
      const summary = result?.[0];
      window.alert(
        summary
          ? `Replenishment evaluated: ${summary.inventory_reorders} stock reorder(s), ${summary.scheduled_reorders} recurring reorder(s), ${summary.alerts_opened} new alert(s).`
          : "Replenishment evaluation completed.",
      );
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }

  async function reorder(row: InventoryItem) {
    setBusy(row.id);
    try {
      await actions.raiseInventoryReorder(organisation.id, row.id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }

  async function issue(row: InventoryItem) {
    const value = window.prompt(`Quantity to issue from ${row.name}`);
    const quantity = Number(value);
    if (!value || !Number.isFinite(quantity) || quantity <= 0) return;
    const notes = window.prompt("Issue reason, department or job reference");
    if (!notes) return;
    setBusy(row.id);
    try {
      await actions.recordInventoryIssue(organisation.id, row.id, quantity, notes);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }

  async function adjust(row: InventoryItem) {
    const value = window.prompt(
      `Stock-take correction for ${row.name} (positive or negative)`,
    );
    const quantity = Number(value);
    if (!value || !Number.isFinite(quantity) || quantity === 0) return;
    const notes = window.prompt("Reason for this stock correction");
    if (!notes) return;
    setBusy(row.id);
    try {
      await actions.adjustInventory(
        organisation.id,
        row.id,
        quantity,
        notes,
      );
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }

  async function toggleAutomation(row: InventoryItem) {
    const enable = !row.auto_reorder;
    const autoSubmit =
      enable &&
      window.confirm(
        "Automatically submit generated requisitions for approval? Cancel keeps them as drafts.",
      );
    setBusy(row.id);
    try {
      await actions.configureInventoryReplenishment(
        organisation.id,
        row.id,
        Number(row.reorder_point),
        Number(row.target_stock),
        enable,
        autoSubmit,
      );
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="STOCK CONTROL"
        title="Inventory & replenishment"
        description="Monitor stock plus inbound supply, generate controlled demand at the reorder point and retain the complete movement-to-requisition trail."
      >
        {can("requisition") && (
          <Button
            variant="outline"
            disabled={busy === "cycle"}
            onClick={runCycle}
          >
            <Repeat2 className="size-4" />
            Evaluate reorders
          </Button>
        )}
        {can("receive") && (
          <>
            <Button className="min-h-11" variant="outline" onClick={() => setImporting(true)}>
              <FileUp className="size-4" />
              Import items
            </Button>
            <Button className="min-h-11" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              Add stock item
            </Button>
          </>
        )}
      </PageHeader>
      {filtered.length ? (
        <Table
          heads={[
            "Item",
            "Location",
            "On hand",
            "Inbound",
            "Reorder / target",
            "Recommended",
            "Control",
            "Action",
          ]}
        >
          {filtered.map((row) => {
            const low =
              row.alert_status === "open" ||
              Number(row.on_hand) <= Number(row.reorder_point);
            return (
              <tr key={row.id || row.inventory_item_id}>
                <td>
                  <strong>{row.name}</strong>
                  <small className="block text-slate-500">
                    {row.sku || "No SKU"} · {row.unit}
                  </small>
                  {row.failure_reason && (
                    <small className="block text-red-700">
                      {row.failure_reason}
                    </small>
                  )}
                </td>
                <td>{row.location_name || row.locations?.name}</td>
                <td>
                  {row.on_hand} {row.unit}
                </td>
                <td>
                  {Number(row.inbound_quantity || 0)} {row.unit}
                </td>
                <td>
                  {row.reorder_point} / {row.target_stock}
                </td>
                <td>
                  {Number(row.recommended_quantity || 0)} {row.unit}
                </td>
                <td>
                  <Badge value={row.failure_reason ? "exception" : low ? "exception" : "active"} />
                  <small className="mt-1 block text-slate-500">
                    {row.auto_reorder
                      ? row.auto_submit
                        ? "Auto · submits for approval"
                        : "Auto · creates draft"
                      : "Manual"}
                  </small>
                </td>
                <td>
                  <div className="flex flex-wrap gap-2">
                    {can("receive") && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy === row.id}
                          onClick={() => issue(row)}
                        >
                          Issue
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy === row.id}
                          onClick={() => adjust(row)}
                        >
                          Correct
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy === row.id}
                          onClick={() => toggleAutomation(row)}
                        >
                          {row.auto_reorder ? "Disable auto" : "Enable auto"}
                        </Button>
                      </>
                    )}
                    {can("requisition") && low && (
                      <Button
                        size="sm"
                        disabled={busy === row.id}
                        onClick={() => reorder(row)}
                      >
                        Create reorder
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </Table>
      ) : (
        <Empty
          title="No stock items"
          description="Add operational stock and link it to an active contracted rate to enable controlled replenishment."
        />
      )}
      <InventoryDialog
        open={creating}
        onOpenChange={setCreating}
        organisationId={organisation.id}
        locations={locations || []}
        contracts={contracts || []}
        onDone={changed}
      />
      <ImportWizard
        open={importing}
        onOpenChange={setImporting}
        organisationId={organisation.id}
        importKind="inventory"
        onDone={changed}
      />
    </>
  );
}

function InventoryDialog({
  open,
  onOpenChange,
  organisationId,
  locations,
  contracts,
  onDone,
}: DialogProps & {
  locations: Awaited<ReturnType<typeof listLocations>>;
  contracts: Contract[];
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const items = contracts
    .filter((c) => c.status === "active")
    .flatMap((c) =>
      (c.contract_items || []).map((i) => ({
        ...i,
        label: `${c.contract_no} · ${i.description}`,
      })),
    );
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createInventoryItem(organisationId, {
        p_location_id: form.get("location"),
        p_contract_item_id: form.get("contractItem") || null,
        p_sku: form.get("sku"),
        p_name: form.get("name"),
        p_specification: form.get("specification"),
        p_unit: form.get("unit"),
        p_opening_stock: Number(form.get("opening")),
        p_reorder_point: Number(form.get("reorderPoint")),
        p_target_stock: Number(form.get("target")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="STOCK CONTROL" title="Add inventory item" />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Name
              <Input name="name" required />
            </Label>
            <Label>
              SKU
              <Input name="sku" />
            </Label>
            <Label>
              Location
              <Select name="location" required>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Unit
              <Input name="unit" required />
            </Label>
            <Label>
              Contracted rate
              <Select name="contractItem">
                <option value="">No linked contract</option>
                {items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Opening stock
              <Input
                name="opening"
                type="number"
                min="0"
                step="0.01"
                defaultValue="0"
                required
              />
            </Label>
            <Label>
              Reorder point
              <Input
                name="reorderPoint"
                type="number"
                min="0"
                step="0.01"
                defaultValue="0"
                required
              />
            </Label>
            <Label>
              Target stock
              <Input
                name="target"
                type="number"
                min="0"
                step="0.01"
                defaultValue="0"
                required
              />
            </Label>
          </div>
          <Label>
            Specification
            <Textarea name="specification" required />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              Add stock item
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
